/**
 * The backend's numeric error catalog, mapped to i18n keys.
 *
 * Every Noor service answers a failure with `{ error: { code, message,
 * details } }` where `code` is a number from a shared catalog (see
 * `error-body.ts` for the envelope shapes). That `message` is written for
 * operators, in one language, and is never safe to show a merchant or a
 * customer as-is — so the CODE, not the message, is what the UI translates.
 *
 * This module is the single place the numbers live. `error-message.ts` turns a
 * code into a translated string; the apps supply the actual wording under their
 * `errors` namespace, keyed by the slugs below.
 *
 * Codes are grouped by service, matching the backend's own catalog:
 *
 *   1xxx  common (available to every service)
 *   3xxx  order-service
 *   4xxx  catalog-service (incl. moderation 46xx/47xx and provider import)
 *   5xxx  auth-service
 *   6xxx  dispatch-service
 *   7xxx  minio-service
 *
 * Retired codes are recorded in `RETIRED_ERROR_CODES` rather than deleted, so a
 * number is never silently reused for a different meaning.
 */

/** Slug identifying an error's message. The `errors` i18n namespace's keys. */
export type ErrorCodeKey = (typeof ERROR_CODE_KEYS)[keyof typeof ERROR_CODE_KEYS];

/**
 * Numeric backend code → i18n key under the `errors` namespace.
 *
 * Keys are grouped by the entity they concern rather than flattened, because a
 * caller that wants to react to a *class* of failure (any "forbidden", any
 * "not found") reads better against grouped names.
 */
export const ERROR_CODE_KEYS = {
  // ── Common — every service can return these ───────────────────────────────
  1000: 'common.internal',
  1001: 'common.unauthenticated',
  1002: 'common.permissionDenied',
  1003: 'common.unavailable',
  1004: 'common.alreadyExists',
  1005: 'common.conflict',
  1006: 'common.validation',

  // ── Order service — order ─────────────────────────────────────────────────
  3000: 'order.notFound',
  3001: 'order.invalidStatus',
  3002: 'order.invalidTotalPrice',
  3009: 'order.decisionAlreadyMade',
  3010: 'order.decisionWindowExpired',
  3011: 'order.creationInProgress',
  3014: 'order.idempotencyKeyReused',

  // ── Order service — basket ────────────────────────────────────────────────
  3003: 'basket.notFound',
  3004: 'basket.empty',
  3005: 'basket.restaurantMismatch',
  3017: 'basket.tooMany',
  3018: 'basket.refRequired',
  3020: 'basket.busy',

  // ── Order service — menu availability ─────────────────────────────────────
  3019: 'order.restaurantClosed',
  3032: 'order.restaurantNotAcceptingOrders',
  3021: 'order.productUnavailable',

  // ── Order service — delivery & branch assignment ──────────────────────────
  3012: 'order.noRestaurantForAddress',
  3013: 'order.branchAssignmentUnavailable',
  3015: 'order.notDeliverable',
  3016: 'order.vendorBalanceInsufficient',

  // ── Order service — payment, settings, generic ────────────────────────────
  3006: 'order.paymentAttemptInProgress',
  3007: 'order.invalidServiceFeeRate',
  3008: 'order.invalidInput',

  // ── Catalog — product ─────────────────────────────────────────────────────
  4000: 'product.notFound',
  4100: 'product.invalid',
  4200: 'product.invalidStatus',
  4014: 'product.modifierGroupNotFound',
  4015: 'product.modifierOptionNotFound',

  // ── Catalog — product category ────────────────────────────────────────────
  4001: 'productCategory.notFound',
  4101: 'productCategory.invalid',
  4300: 'productCategory.duplicateName',
  4400: 'productCategory.forbidden',

  // ── Catalog — product within a branch ─────────────────────────────────────
  4011: 'restaurantProduct.notFound',
  4114: 'restaurantProduct.invalid',
  4401: 'restaurantProduct.forbidden',

  // ── Catalog — restaurant (branch) ─────────────────────────────────────────
  4004: 'restaurant.notFound',
  4104: 'restaurant.invalid',
  4113: 'restaurant.invalidWorkingHours',
  4201: 'restaurant.invalidStatus',
  4403: 'restaurant.forbidden',
  4012: 'restaurant.outOfCoverageZone',

  // ── Catalog — restaurant category ─────────────────────────────────────────
  4002: 'restaurantCategory.notFound',
  4102: 'restaurantCategory.invalid',

  // ── Catalog — merchant ────────────────────────────────────────────────────
  4008: 'merchant.notFound',
  4110: 'merchant.invalid',
  4407: 'merchant.forbidden',

  // ── Catalog — delivery pricing ────────────────────────────────────────────
  4109: 'deliveryPricing.invalid',
  4202: 'deliveryPricing.notCalculable',

  // ── Catalog — promotions ──────────────────────────────────────────────────
  4007: 'promotion.notFound',
  4107: 'promotion.invalid',
  4402: 'promotion.forbidden',

  // ── Catalog — restaurant banners ──────────────────────────────────────────
  4009: 'restaurantBanner.notFound',
  4111: 'restaurantBanner.invalid',
  4404: 'restaurantBanner.forbidden',

  // ── Catalog — product banners ─────────────────────────────────────────────
  4010: 'productBanner.notFound',
  4112: 'productBanner.invalid',
  4405: 'productBanner.forbidden',

  // ── Catalog — images ──────────────────────────────────────────────────────
  4013: 'image.notFound',
  4116: 'image.invalid',

  // ── Catalog — translations ────────────────────────────────────────────────
  4005: 'translation.notFound',
  4105: 'translation.invalid',

  // ── Catalog — reviews ─────────────────────────────────────────────────────
  4115: 'review.invalid',
  4203: 'review.orderNotEligible',
  4204: 'review.windowExpired',
  4301: 'review.alreadyExists',
  4406: 'review.forbidden',
  4500: 'review.orderServiceUnavailable',

  // ── Catalog — generic request validation ──────────────────────────────────
  4108: 'catalog.invalidInput',

  // ── Catalog — moderation (change requests) ────────────────────────────────
  4600: 'changeRequest.notFound',
  4601: 'changeRequest.forbidden',
  4602: 'changeRequest.notPending',
  4604: 'changeRequest.noApplier',
  4605: 'changeRequest.alreadyPending',
  4701: 'changeRequest.invalid',

  // ── Catalog — provider menu import ────────────────────────────────────────
  4016: 'import.jobNotFound',
  4017: 'import.providerMenuNotFound',
  4205: 'import.integrationNotConfigured',
  4206: 'import.integratorNotImplemented',
  4408: 'import.managedEntity',
  4501: 'import.serviceUnavailable',

  // ── Auth service ──────────────────────────────────────────────────────────
  5000: 'auth.validation',
  5001: 'auth.invalidCredentials',
  5004: 'auth.userBanned',
  5005: 'auth.forbiddenAction',
  9402: 'auth.forbiddenActionByRole',
  5006: 'auth.invalidToken',
  5008: 'auth.userNotFound',
  5011: 'auth.userNotActivated',
  5012: 'auth.identitySuspended',

  // ── Dispatch — delivery ───────────────────────────────────────────────────
  6001: 'dispatch.invalid',
  6002: 'dispatch.unsupportedProvider',
  6100: 'dispatch.notFound',
  6200: 'dispatch.alreadyExists',
  6201: 'dispatch.invalidStatus',
  6101: 'dispatch.restaurantNotFound',

  // ── Dispatch — Delever onboarding ─────────────────────────────────────────
  6004: 'dispatch.invalidOnboarding',
  6103: 'dispatch.noDeleverRestaurants',
  6300: 'dispatch.deleverCredentialsInvalid',
  6104: 'dispatch.providerMenuNotFound',

  // ── Dispatch — generic request validation ─────────────────────────────────
  6000: 'dispatch.invalidInput',

  // ── Minio — upload request validation ─────────────────────────────────────
  7000: 'upload.filenameRequired',
  7001: 'upload.filenameNoExtension',
  7002: 'upload.countInvalid',
  7003: 'upload.batchTooLarge',
  7004: 'upload.folderNotAllowed',
  7005: 'upload.bucketNotAllowed',
} as const satisfies Record<number, string>;

/**
 * Codes the backend has withdrawn. Listed so nobody re-uses the number for a
 * new meaning, and so a stale deployment still answering one of them resolves
 * to the generic fallback rather than a wrong message.
 *
 *   4003  ErrBannerNotFound          split into restaurant/product banners
 *   4006  ErrCredentialNotFound      Delever credentials moved to dispatch
 *   4106  ErrInvalidCredentialInput  ditto
 *   4603  ErrChangeRequestModified   requests are immutable until terminal
 *   4700  ErrChangeRequestBatchTooLarge  payload-size limit never enabled
 *   6003  ErrInvalidCredentialInput  from the removed legacy CredentialService
 *   6102  ErrCredentialNotFound      ditto
 */
export const RETIRED_ERROR_CODES: readonly number[] = [
  4003, 4006, 4106, 4603, 4700, 6003, 6102,
];

/**
 * The i18n key for a backend code, or `undefined` when the code is unknown to
 * this catalog — a retired code, or one added by a backend newer than this
 * build. Callers fall back to their own message in that case.
 *
 * Accepts the string form because that is what `ApiError.code` carries
 * (`error-body.ts` stringifies the numeric code).
 */
export function errorCodeKey(
  code: string | number | undefined,
): ErrorCodeKey | undefined {
  if (code === undefined) return undefined;
  const numeric = typeof code === 'number' ? code : Number(code);
  if (!Number.isInteger(numeric)) return undefined;
  return (ERROR_CODE_KEYS as Record<number, ErrorCodeKey | undefined>)[numeric];
}
